import React, { useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const TermsContainer = styled.div`
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', sans-serif;
  color: #333;
  line-height: 1.7;
`;

const TermsHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const TermsTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: #2563eb;
  margin-bottom: 15px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const TermsSubtitle = styled.p`
  font-size: 20px;
  color: #555;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const EffectiveDate = styled.div`
  background-color: #f0f4f8;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 40px;
  text-align: center;
  font-weight: 600;
  color: #2563eb;
  font-size: 18px;
`;

const ContentSection = styled(motion.section)`
  margin-bottom: 40px;
  padding: 30px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  line-height: 1.7;
  font-size: 17px;
  color: #444;

  p {
    margin-bottom: 20px;
  }

  h2 {
    font-size: 24px;
    color: #2563eb;
    margin-bottom: 20px;
    font-weight: 700;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 10px;
  }

  h3 {
    font-size: 20px;
    color: #374151;
    margin-bottom: 15px;
    font-weight: 600;
  }

  ul {
    margin-bottom: 20px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 10px;
  }

  strong {
    color: #2563eb;
    font-weight: 700;
  }

  a {
    color: #2563eb;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Superscript = styled.sup`
  font-size: 0.6em;
  font-weight: 700;
  vertical-align: super;
  line-height: 0;
  color: inherit;
  position: relative;
  top: -0.2em;
`;

const TermsOfService = () => {
  const containerRef = useRef(null);

  // Use useLayoutEffect to scroll immediately before paint
  useLayoutEffect(() => {
    // Scroll window to top immediately
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also scroll container into view if it exists
    if (containerRef.current) {
      try {
        containerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      } catch (e) {
        // Fallback for older browsers
        containerRef.current.scrollIntoView(true);
      }
    }
  }, []);

  // Also use useEffect as backup after render
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (containerRef.current) {
        try {
          containerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
        } catch (e) {
          // Fallback for older browsers
          containerRef.current.scrollIntoView(true);
        }
      }
    };
    
    // Immediate scroll
    scrollToTop();
    
    // Also try after a small delay
    const timer = setTimeout(scrollToTop, 10);
    const timer2 = setTimeout(scrollToTop, 100);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <TermsContainer ref={containerRef}>
      <TermsHeader>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <TermsTitle>Terms of Service</TermsTitle>
          <TermsSubtitle>
            Terms and conditions for using M²eBooks services
          </TermsSubtitle>
        </motion.div>
      </TermsHeader>

      <EffectiveDate
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        Effective Date: 09/21/2025
      </EffectiveDate>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <p>
          Welcome to M<Superscript>2</Superscript>ebooks ("M<Superscript>2</Superscript>e")! These Terms of Use ("Terms") govern your access to and use of the M<Superscript>2</Superscript>e website, mobile application, and digital services (collectively, the "Services"), operated by M<Superscript>2</Superscript>eBooks, LLC. ("M<Superscript>2</Superscript>e", "we", "our" or "us"). By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy, which is incorporated by reference. If you do not agree with these Terms, you may not access or use our Services.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <h2>1. Eligibility</h2>
        <p>
          You must be at least 18 years old or the legal age of majority in your jurisdiction to create an account or use our Services. By using the Services, you represent and warrant that you meet these requirements.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <h2>2. User Account</h2>
        <p>
          To access certain features (including purchasing or downloading M<Superscript>2</Superscript>eBooks), you may need to create an account. You agree to:
        </p>
        <ul>
          <li>Provide accurate, current, and complete information.</li>
          <li>Maintain the security of your account credentials.</li>
          <li>Be responsible for all activities that occur under your account.</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate your account at our discretion, including for suspected fraud or violation of these Terms.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0 }}
      >
        <h2>3. Digital Content and License</h2>
        <p>
          All M<Superscript>2</Superscript>eBooks and digital content ("Content") available through the Services are licensed to you, not sold, and are subject to the following:
        </p>
        <ul>
          <li>You receive a limited, non-exclusive, non-transferable, revocable license to access and use the Content for personal, non-commercial purposes.</li>
          <li>You may not reproduce, distribute, publicly display, sell, rent, modify, create derivative works, or otherwise exploit the Content without express written permission from M<Superscript>2</Superscript>e or the applicable rights holder.</li>
        </ul>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        <h2>4. Payments and Refunds</h2>
        <p>
          Prices for M<Superscript>2</Superscript>eBooks and subscriptions are displayed on the platform and are subject to change. All purchases are processed securely through authorized third-party providers.
        </p>
        <h3>Refund Policy:</h3>
        <p>
          Due to the digital nature of our products, all sales are final unless otherwise required by law. If you experience issues with a download or corrupted file, contact support at <a href="mailto:help@M2ebooks.com">help@M2ebooks.com</a>.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.4 }}
      >
        <h2>5. Restrictions on Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Services for any illegal purpose.</li>
          <li>Interfere with or disrupt the integrity of the Services.</li>
          <li>Reverse engineer, decompile, or disassemble any part of the Services.</li>
          <li>Circumvent DRM or access control mechanisms related to our Content.</li>
        </ul>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.6 }}
      >
        <h2>6. Intellectual Property</h2>
        <p>
          All materials provided by M<Superscript>2</Superscript>e, including the site/app design, text, graphics, logos, icons, and software, are the property of M<Superscript>2</Superscript>e or its licensors and are protected by intellectual property laws. All rights not expressly granted herein are reserved.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.8 }}
      >
        <h2>7. Termination</h2>
        <p>
          We may suspend or terminate your access to the Services at any time for any reason, including violation of these Terms. Upon termination, your right to use the Services and access purchased Content may be revoked, subject to applicable laws.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.0 }}
      >
        <h2>8. Third-Party Services and Links</h2>
        <p>
          The Services may contain links to third-party websites or services that are not owned or controlled by M<Superscript>2</Superscript>e. We are not responsible for the content or practices of any third party.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.2 }}
      >
        <h2>9. Disclaimers and Limitation of Liability</h2>
        <p>
          The Services and all content are provided "as is" without warranties of any kind. M<Superscript>2</Superscript>e does not guarantee uninterrupted or error-free access. To the maximum extent permitted by law, M<Superscript>2</Superscript>e shall not be liable for any indirect, incidental, special, or consequential damages resulting from your use of the Services or content.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.4 }}
      >
        <h2>10. Governing Law</h2>
        <p>
          These Terms are governed by the laws of Texas, USA without regard to conflict of law principles. You agree to submit to the exclusive jurisdiction of the courts located in Texas.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.6 }}
      >
        <h2>11. Changes to Terms</h2>
        <p>
          We may modify these Terms from time to time. If we make material changes, we will notify you via email or through the Services. Your continued use of the Services constitutes acceptance of the updated Terms.
        </p>
      </ContentSection>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 2.8 }}
      >
        <h2>12. Contact Us</h2>
        <p>
          If you have questions about these Terms, please contact us at:
        </p>
        <p>
          Email: <a href="mailto:help@M2ebooks.com">help@M2ebooks.com</a>
        </p>
      </ContentSection>
    </TermsContainer>
  );
};

export default TermsOfService;

